import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Sparkles, Loader2 } from "lucide-react";

/**
 * Confirmation modal shown after a PDF/image is parsed.
 * Lets the parent/teacher review detected words, remove bad ones,
 * and add missing ones BEFORE any TTS / image generation is triggered.
 *
 * Props:
 *   open: boolean
 *   detectedWords: string[]           // words returned by /api/extract-words
 *   suggestedName: string             // file-based default name
 *   source: "pdf" | "image"           // for the headline label
 *   submitting: boolean               // disables Confirm while POSTing
 *   nameLabel: string                 // "List name" / "Pack name"
 *   onCancel: () => void
 *   onConfirm: ({ name, words }) => Promise<void>
 *   extraFields?: ReactNode           // optional admin-only form fields (grade, school, voice)
 */
export default function WordConfirmModal({
  open,
  detectedWords = [],
  suggestedName = "",
  source = "pdf",
  submitting = false,
  nameLabel = "List name",
  onCancel,
  onConfirm,
  extraFields = null,
}) {
  const [name, setName] = useState(suggestedName);
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState("");

  // Reset state when the modal opens with new data
  useEffect(() => {
    if (open) {
      setName(suggestedName || "");
      setWords(detectedWords.map((w) => String(w).toLowerCase()));
      setNewWord("");
    }
  }, [open, detectedWords, suggestedName]);

  const cleanedWords = useMemo(
    () => words.map((w) => w.trim().toLowerCase()).filter(Boolean),
    [words]
  );

  if (!open) return null;

  const sourceLabel = source === "image" ? "image" : "PDF";

  const removeAt = (idx) => setWords((prev) => prev.filter((_, i) => i !== idx));

  const addWord = () => {
    const t = newWord.trim().toLowerCase().replace(/[^a-z]/g, "");
    if (!t) return;
    if (cleanedWords.includes(t)) {
      setNewWord("");
      return;
    }
    setWords((prev) => [...prev, t]);
    setNewWord("");
  };

  const onAddKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addWord();
    }
  };

  const handleConfirm = async () => {
    if (!cleanedWords.length || submitting) return;
    await onConfirm({ name: name.trim() || suggestedName || "My spelling list", words: cleanedWords });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
        data-testid="word-confirm-modal"
        onClick={submitting ? undefined : onCancel}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", bounce: 0.25 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b-4 border-blue-50 flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#FBBF24] flex items-center justify-center shadow-[0_4px_0_#B45309] shrink-0">
              <Sparkles className="w-6 h-6 text-[#1E1B4B]" strokeWidth={3} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-[#1E1B4B]">
                Review detected words
              </h2>
              <p className="text-sm text-slate-600 font-semibold mt-0.5">
                We read your {sourceLabel} and pulled out{" "}
                <span className="text-[#1E1B4B]">{cleanedWords.length}</span>{" "}
                spelling word{cleanedWords.length === 1 ? "" : "s"}. Add or remove
                before we make audio &amp; pictures.
              </p>
            </div>
            <button
              onClick={onCancel}
              disabled={submitting}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-40"
              data-testid="word-confirm-close-btn"
              aria-label="Cancel"
            >
              <X className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                {nameLabel}
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={suggestedName || "Untitled"}
                className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold"
                data-testid="word-confirm-name-input"
              />
            </div>

            {extraFields ? <div className="space-y-2">{extraFields}</div> : null}

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Words ({cleanedWords.length})
              </label>
              {words.length === 0 ? (
                <div className="text-sm text-slate-500 italic font-semibold py-2">
                  No words yet — add some below.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="word-confirm-chip-list">
                  {words.map((w, i) => (
                    <span
                      key={`${w}-${i}`}
                      className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-blue-50 border-2 border-blue-100 text-[#1E1B4B] text-sm font-bold"
                      data-testid={`word-confirm-chip-${w}`}
                    >
                      {w}
                      <button
                        onClick={() => removeAt(i)}
                        disabled={submitting}
                        className="w-5 h-5 rounded-full bg-white text-[#F43F5E] hover:bg-rose-50 flex items-center justify-center"
                        data-testid={`word-confirm-remove-${w}`}
                        aria-label={`Remove ${w}`}
                      >
                        <X className="w-3 h-3" strokeWidth={4} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                Add a word
              </label>
              <div className="flex gap-2">
                <input
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  onKeyDown={onAddKey}
                  placeholder="Type a word and press Enter"
                  className="flex-1 rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold"
                  data-testid="word-confirm-add-input"
                />
                <button
                  type="button"
                  onClick={addWord}
                  disabled={submitting || !newWord.trim()}
                  className="px-4 py-2 rounded-2xl bg-[#4D96FF] text-white font-bold shadow-[0_3px_0_#1D4ED8] active:translate-y-[2px] active:shadow-[0_1px_0_#1D4ED8] disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="word-confirm-add-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={3} /> Add
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t-4 border-blue-50 flex flex-wrap items-center justify-end gap-2 bg-slate-50">
            <button
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 rounded-2xl bg-white border-4 border-slate-200 hover:border-slate-300 text-slate-700 font-bold disabled:opacity-50"
              data-testid="word-confirm-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting || cleanedWords.length === 0}
              className="px-5 py-2 rounded-2xl bg-[#22C55E] text-white font-bold shadow-[0_4px_0_#15803D] active:translate-y-[2px] active:shadow-[0_2px_0_#15803D] disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="word-confirm-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> Saving…
                </>
              ) : (
                <>Confirm &amp; create ({cleanedWords.length})</>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
